'use client';

import {
    type HTMLAttributeAnchorTarget,
    type MouseEventHandler,
    type ReactElement,
    type ReactNode,
} from 'react';
import BaseLink, { type LinkProps } from 'next/link';

export interface Props extends LinkProps {
    id?: string;
    children?: ReactNode;
    className?: string;
    isSponsored?: boolean;
    target?: HTMLAttributeAnchorTarget;
    hardRefresh?: boolean;
}

export const Link = ({ isSponsored, target, id, ...props }: Props) => {
    const newProps: LinkProps = { ...props, passHref: true, prefetch: false };

    if (props.shallow === undefined) newProps.shallow = false;
    if (props.prefetch === undefined) newProps.prefetch = false;
    if (props.locale === undefined) newProps.locale = false;

    if (isSponsored) {
        newProps.href = String(props.href).replace(/\[/g, '%5B').replace(/\]/g, '%5D');
        return (
            <BaseLink rel="sponsored nofollow" target="_blank" className={props.className} {...newProps}>
                {props.children}
            </BaseLink>
        );
    }

    if ((props.children as ReactElement)?.type === 'a') {
        // FIXME: get rid of legacyBehavior
        return (
            <BaseLink {...newProps} legacyBehavior>
                {props.children}
            </BaseLink>
        );
    }

    const onClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
        props.onClick?.(e);

        if (props.hardRefresh && typeof props.href === 'string') {
            e.preventDefault();
            e.stopPropagation();

            if (target === '_blank') {
                window.open(props.href, '_blank', 'noopener,noreferrer');
            } else {
                window.location.href = props.href;
            }
        }
    };

    return (
        <BaseLink {...newProps} legacyBehavior>
            <a id={id} className={props.className} target={target} onClick={onClick}>
                {props.children}
            </a>
        </BaseLink>
    );
};
